import { generateProcessDiagramSVG } from '@process-advisor/lib/goJsDiagramGenerator';
import { loadEventLogFromDatabase, hasFilters } from '@process-advisor/lib/eventLogLoader';

/**
 * Load event log for export
 * Uses the consolidated event log loader
 * 
 * @param {string} modelId - The analysis model ID
 * @param {string|Array} modelFilters - The filters field from analysis model
 * @param {Array} taskAttributes - Task attributes from the model
 * @param {Function} loadForgeModule - Function to load Forge invoke module
 */
async function loadEventLogForExport(modelId, modelFilters, taskAttributes, loadForgeModule) {
  const { invoke } = await loadForgeModule();
  
  try {
    const useFilteredData = hasFilters(modelFilters);
    
    console.log(`[EXPORT] Loading ${useFilteredData ? 'filtered' : 'full'} event log for model ${modelId}`);
    
    // Use the consolidated event log loader
    const eventLogArray = await loadEventLogFromDatabase(
      modelId,
      taskAttributes || [],
      invoke,
      useFilteredData,  // Load filtered if model has filters
      false,            // Not external data
      ''                // No external fields
    );
    
    console.log(`[EXPORT] Event log loaded successfully: ${eventLogArray.length} events`);
    
    return eventLogArray;
    
  } catch (error) {
    console.error('[EXPORT] Error loading event log:', error);
    throw error;
  }
}

/**
 * Generates a complete standalone HTML report with embedded diagram and analytics
 */
export function generateProcessReportHTML(model, variants, steps, issues, diagramData, totalSteps, processLabel = 'Process', entityLabel = 'Work Item', taskArray = [], variantArray = [], taskAttributes = [], metricsData = null) {
  // Generate the Go.js diagram SVG - using hardcoded optimal settings
  const diagramSVG = generateProcessDiagramSVG(diagramData);
  
  // Take top 20 variants and 20 steps
  const topVariants = variants.slice(0, 20);
  const topSteps = steps.slice(0, 20);
  
  // Get model name
  const modelName = model?.name || 'Data Model';
  
  // Calculate summary metrics
  const totalWorkItems = variants.reduce((sum, v) => sum + (v.tasks || 0), 0); // Sum of all variant task counts
  const totalEvents = variants.reduce((sum, v) => sum + (v.events || 0), 0); // Sum events from all variants
  const totalVariants = variants.length;
  const totalStepsCount = totalSteps ?? steps.length; // Use full count if provided, else fall back
  const totalActivities = diagramData.nodeDataArray
    ? diagramData.nodeDataArray.filter(n => n.start_node !== true && n.end_node !== true).length
    : 0;
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(modelName)} - Process Analysis Report</title>
  <style>
    * { box-sizing: border-box; }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 0;
      background: #F4F5F7;
      color: #172B4D;
      font-size: 14px;
      line-height: 1.5;
    }
    
    .container {
      max-width: 1400px;
      margin: 0 auto;
      background: white;
    }
    
    /* Simple Header */
    .header {
      background: linear-gradient(135deg, #0052CC 0%, #0065FF 100%);
      color: white;
      padding: 20px 30px;
    }
    
    .header h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
    }
    
    /* Content */
    .content {
      padding: 24px 30px;
    }
    
    h2 {
      font-size: 18px;
      font-weight: 600;
      margin: 24px 0 12px 0;
      padding-bottom: 8px;
      border-bottom: 2px solid #0052CC;
      color: #172B4D;
    }
    
    h2:first-child {
      margin-top: 0;
    }
    
    .info {
      background: #E9F2FF;
      border-left: 3px solid #0052CC;
      padding: 10px 14px;
      margin: 0 0 16px 0;
      font-size: 13px;
      color: #172B4D;
    }
    
    /* Diagram */
    .diagram-wrapper {
      background: #FAFBFC;
      border: 1px solid #DFE1E6;
      border-radius: 4px;
      padding: 16px;
      margin-bottom: 24px;
      overflow-x: auto;
      overflow-y: auto;
      min-height: 1200px;
      max-height: 1800px;
    }
    
    .diagram-wrapper svg {
      display: block;
      width: 100%;
      height: auto;
      min-height: 1000px;
    }
    
    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border: 1px solid #DFE1E6;
      margin-bottom: 24px;
    }
    
    thead {
      background: #F4F5F7;
    }
    
    th {
      padding: 10px 12px;
      text-align: left;
      font-size: 11px;
      font-weight: 600;
      color: #172B4D;
      text-transform: uppercase;
      border-bottom: 2px solid #0052CC;
      white-space: nowrap;
    }
    
    th.right {
      text-align: right;
    }
    
    td {
      padding: 10px 12px;
      font-size: 13px;
      border-bottom: 1px solid #DFE1E6;
    }
    
    td.right {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    
    tbody tr:hover {
      background: #F4F5F7;
    }
    
    tbody tr:last-child td {
      border-bottom: none;
    }
    
    .variant-path {
      color: #0052CC;
    }
    
    .percentage {
      color: #0052CC;
      font-weight: 500;
    }
    
    .duration {
      color: #00875A;
      font-weight: 500;
    }
    
    .footer {
      text-align: center;
      padding: 20px;
      color: #6B778C;
      font-size: 12px;
      border-top: 1px solid #DFE1E6;
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      <h1>${escapeHtml(modelName)}</h1>
      <div style="font-size: 12px; opacity: 0.85; margin-top: 4px;">
        Process Analysis Report - Generated ${new Date().toLocaleDateString()}
      </div>
    </div>

    <div class="content">
      <!-- Key Metrics -->
      <h2>📊 Key Metrics</h2>
      <div style="display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px;">
        <div style="background: #F4F5F7; padding: 12px 16px; border-radius: 4px; border: 1px solid #DFE1E6; min-width: 120px;">
          <div style="font-size: 10px; color: #6B778C; text-transform: uppercase; margin-bottom: 2px;">Events</div>
          <div style="font-size: 20px; font-weight: 600; color: #172B4D;">${totalEvents.toLocaleString()}</div>
        </div>
        <div style="background: #F4F5F7; padding: 12px 16px; border-radius: 4px; border: 1px solid #DFE1E6; min-width: 120px;">
          <div style="font-size: 10px; color: #6B778C; text-transform: uppercase; margin-bottom: 2px;">${entityLabel + 's'}</div>
          <div style="font-size: 20px; font-weight: 600; color: #172B4D;">${totalWorkItems.toLocaleString()}</div>
        </div>
        <div style="background: #F4F5F7; padding: 12px 16px; border-radius: 4px; border: 1px solid #DFE1E6; min-width: 120px;">
          <div style="font-size: 10px; color: #6B778C; text-transform: uppercase; margin-bottom: 2px;">Variants</div>
          <div style="font-size: 20px; font-weight: 600; color: #172B4D;">${totalVariants}</div>
        </div>
        <div style="background: #F4F5F7; padding: 12px 16px; border-radius: 4px; border: 1px solid #DFE1E6; min-width: 120px;">
          <div style="font-size: 10px; color: #6B778C; text-transform: uppercase; margin-bottom: 2px;">Steps</div>
          <div style="font-size: 20px; font-weight: 600; color: #172B4D;">${totalStepsCount}</div>
        </div>
        <div style="background: #F4F5F7; padding: 12px 16px; border-radius: 4px; border: 1px solid #DFE1E6; min-width: 120px;">
          <div style="font-size: 10px; color: #6B778C; text-transform: uppercase; margin-bottom: 2px;">Activities</div>
          <div style="font-size: 20px; font-weight: 600; color: #172B4D;">${totalActivities}</div>
        </div>
      </div>

      ${generateMetricsSection(metricsData, processLabel, entityLabel)}

      <!-- Diagram -->
      <h2>📊 Process Flow Diagram</h2>
      <div class="info">Visual representation of the top 20 process variants showing all unique steps and their relationships.</div>
      <div class="diagram-wrapper">
        ${diagramSVG || '<p style="text-align: center; color: #6B778C; padding: 40px;">Diagram unavailable</p>'}
      </div>

      <!-- Variants Table -->
      <h2>🔀 Top 20 Process Variants</h2>
      <div class="info">${processLabel} variants ordered by ${entityLabel.toLowerCase()} volume, showing the most common paths through your workflow.</div>
      <table>
        <thead>
          <tr>
            <th width="50">#</th>
            <th width="600">Variant Path</th>
            <th width="90" class="right">Count</th>
            <th width="70" class="right">%</th>
            <th width="130" class="right">Avg Duration</th>
            <th width="130" class="right">Min Duration</th>
            <th width="130" class="right">Max Duration</th>
          </tr>
        </thead>
        <tbody>
          ${topVariants.map((v, i) => `
          <tr>
            <td width="50">${i + 1}</td>
            <td width="600" class="variant-path">${formatVariantPath(v.variant || v.name || '')}</td>
            <td width="90" class="right">${(v.tasks || 0).toLocaleString()}</td>
            <td width="70" class="right percentage">${formatPercentage(v.perc_total_tasks)}</td>
            <td width="130" class="right duration">${formatDuration(v.avg_duration)}</td>
            <td width="130" class="right duration">${formatDuration(v.min_duration)}</td>
            <td width="130" class="right duration">${formatDuration(v.max_duration)}</td>
          </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- Steps Table -->
      <h2>➡️ Process Transitions (Top 20 Steps)</h2>
      <div class="info">Individual transitions between activities, showing the most frequent paths in your process.</div>
      <table>
        <thead>
          <tr>
            <th width="50">#</th>
            <th width="300">From Activity</th>
            <th width="300">To Activity</th>
            <th width="90" class="right">Count</th>
            <th width="70" class="right">%</th>
            <th width="130" class="right">Avg Duration</th>
            <th width="130" class="right">Min Duration</th>
            <th width="130" class="right">Max Duration</th>
          </tr>
        </thead>
        <tbody>
          ${topSteps.map((s, i) => `
          <tr>
            <td width="50">${i + 1}</td>
            <td width="300">${escapeHtml(s.from_activity || '')}</td>
            <td width="300">${escapeHtml(s.to_activity || '')}</td>
            <td width="90" class="right">${(s.tasks || 0).toLocaleString()}</td>
            <td width="70" class="right percentage">${formatPercentage(s.perc_total_tasks)}</td>
            <td width="130" class="right duration">${formatDuration(s.avg_duration)}</td>
            <td width="130" class="right duration">${formatDuration(s.min_duration)}</td>
            <td width="130" class="right duration">${formatDuration(s.max_duration)}</td>
          </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- Work Item Detail — Top 5 Variants -->
      ${generateWorkItemDetailSection(variants, taskArray, variantArray, taskAttributes, entityLabel, 5, 5)}

      <div class="footer">
        Generated by AI Process Optimizer | Atlassian Marketplace
      </div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Generates a "Work Item Detail" section for the top N variants.
 *
 * taskAttributes format (from analyticsProcessor):
 *   { id: 'ta0', field: 'Production(s)' | 'customfield_10223', attributeID: 'customfield_10223', schema: 'option' }
 * taskArray rows (from processEventLogTransform) have values at task[attr.id] (ta0, ta1, ...).
 */
function generateWorkItemDetailSection(variants, taskArray, variantArray, taskAttributes, entityLabel = 'Work Item', topVariants = 5, topItemsPerVariant = 5) {
  if (!variants || variants.length === 0 || !taskArray || taskArray.length === 0) return '';

  const selectedVariants = variants.slice(0, topVariants);

  // Determine display label for each task attribute column.
  // field = display name if 4-col format was stored, otherwise it's the Jira field ID.
  // Format Jira field IDs into readable labels: "customfield_10223" → "Field 10223",
  // "assignee" → "Assignee", "priority" → "Priority"
  const formatFieldLabel = (field) => {
    if (!field) return '';
    if (field.startsWith('customfield_')) {
      return 'Field ' + field.replace('customfield_', '');
    }
    return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
  };

  // Only include attrs that have an id (ta0, ta1...) so we can look up values
  const attrCols = (taskAttributes || []).filter(a => a.id && (a.field || a.attributeID));

  const sections = selectedVariants.map((variant, idx) => {
    const variantId = variant.variant_id ?? variant.v;
    const variantName = variant.variant || variant.name || `Variant ${idx + 1}`;
    const taskCount = variant.tasks || 0;

    // Match tasks to this variant by .v (variant_id from processVariantsTable)
    const variantTasks = taskArray
      .filter(t => t.v === variantId && t.filtered !== 1)
      .sort((a, b) => (b.d || 0) - (a.d || 0))
      .slice(0, topItemsPerVariant);

    if (variantTasks.length === 0) return '';

    // Column headers
    const attrHeaders = attrCols.map(a =>
      `<th>${escapeHtml(formatFieldLabel(a.field))}</th>`
    ).join('');

    const headers = `
      <th>${escapeHtml(entityLabel)} #</th>
      <th>Create Time</th>
      <th>Close Time</th>
      <th>Duration</th>
      ${attrHeaders}
    `;

    const rows = variantTasks.map(task => {
      const attrCells = attrCols.map(a => {
        // Values are stored under task[a.id] e.g. task['ta0']
        let val = (task[a.id] !== undefined && task[a.id] !== null && task[a.id] !== 'null') 
          ? String(task[a.id]) : '';
        if (val && (a.schema === 'array' || a.schema === 'stagil_asset_forge')) {
          val = val.replaceAll('^^', ', ');
        }
        return `<td>${escapeHtml(val)}</td>`;
      }).join('');

      return `<tr>
        <td><strong>${escapeHtml(task.k || task.task || '')}</strong></td>
        <td>${escapeHtml((task.t1 || '').substring(0, 10))}</td>
        <td>${escapeHtml((task.t2 || '').substring(0, 10))}</td>
        <td>${formatDuration(task.d || 0)}</td>
        ${attrCells}
      </tr>`;
    }).join('');

    return `
      <div style="margin-bottom: 24px; border: 1px solid #DFE1E6; border-radius: 4px; overflow: hidden;">
        <div style="background: #F4F5F7; padding: 10px 14px; border-bottom: 1px solid #DFE1E6;">
          <span style="font-size: 13px; font-weight: 600; color: #172B4D;">
            Variant ${idx + 1}
          </span>
          <span style="font-size: 12px; color: #6B778C; margin-left: 10px;">
            ${taskCount} ${escapeHtml(entityLabel.toLowerCase())}${taskCount !== 1 ? 's' : ''}
            &bull; Avg ${formatDuration(variant.avg_duration || 0)}
            &bull; Total ${formatDuration(variant.total_duration || 0)}
          </span>
          <div style="font-size: 11px; color: #5E6C84; margin-top: 4px; font-family: monospace; word-break: break-all;">
            ${formatVariantPath(variantName)}
          </div>
        </div>
        <div style="overflow-x: auto;">
          <table style="margin: 0; border-radius: 0;">
            <thead><tr>${headers}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`;
  }).filter(Boolean).join('');

  if (!sections) return '';

  // Note if task attribute columns are all empty (event log predates attribute support)
  const hasAttrData = attrCols.length > 0 && (() => {
    const sample = variants.slice(0, topVariants);
    return sample.some(variant => {
      const variantId = variant.variant_id ?? variant.v;
      const task = taskArray.find(t => t.v === variantId && t.filtered !== 1);
      return task && attrCols.some(a => task[a.id] !== undefined && task[a.id] !== null && task[a.id] !== 'null');
    });
  })();

  const attrNote = attrCols.length > 0 && !hasAttrData
    ? `<div style="font-size:12px;color:#FF8B00;background:#FFFAE6;padding:8px 12px;border-radius:3px;margin-bottom:12px;border:1px solid #FFE380;">
        ⚠️ Attribute columns (${escapeHtml(attrCols.map(a => formatFieldLabel(a.field)).join(', '))}) are empty — 
        please <strong>Refresh</strong> this model from the action menu to regenerate the event log with attribute data.
       </div>`
    : '';

  return `
    <h2>📋 ${escapeHtml(entityLabel)} Detail — Top ${topVariants} Variants</h2>
    <div class="info">
      Top ${topItemsPerVariant} ${escapeHtml(entityLabel.toLowerCase())}s by duration for each of the top ${topVariants} process variants,
      sorted by total duration (longest first).
    </div>
    ${attrNote}
    ${sections}`;
}

function generateMetricsSection(metricsData, processLabel, entityLabel) {
  if (!metricsData) return '';

  const m = metricsData;

  // ── Formatters ────────────────────────────────────────────────────────────
  const fmtTime = (s) => {
    s = s ?? 0;
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), mn = Math.floor((s % 3600) / 60);
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (mn > 0) parts.push(`${mn}m`);
    return parts.length ? parts.join(' ') : '0m';
  };
  const fmtPct = (v) => v != null ? Math.round((v ?? 0) * 100) + '%' : '—';
  const fmtNum = (v) => v != null ? Math.round(v ?? 0).toLocaleString() : '—';
  const fmtDec = (v, dp = 1) => v != null ? (v ?? 0).toFixed(dp) : '—';
  const fmtCT  = (s) => {
    if (!s) return '—';
    const h = s / 3600;
    if (h >= 24) { const d = Math.floor(h / 24); return `${d}d ${Math.floor(h % 24)}h`; }
    if (h >= 1)  { return `${Math.floor(h)}h ${Math.floor((s % 3600) / 60)}m`; }
    return `${Math.floor(s / 60)}m`;
  };
  const val = (v, fmt) => (v != null && v !== 0) ? fmt(v) : '—';

  // ── Anomaly detection ─────────────────────────────────────────────────────
  const anomalies = [];
  const rework = (m.rework_rate ?? 0) * 100;
  if (rework > 25)       anomalies.push({ sev: 'high',   text: `Rework rate of ${Math.round(rework)}% is critically high — more than 1 in 4 ${entityLabel.toLowerCase()}s loops back. This is a strong signal of quality or handoff problems.` });
  else if (rework > 10)  anomalies.push({ sev: 'medium', text: `Rework rate of ${Math.round(rework)}% warrants investigation — items are frequently revisiting earlier states.` });
  const fe = (m.avg_flow_efficiency ?? 0) * 100;
  if (fe > 0 && fe < 15) anomalies.push({ sev: 'high',   text: `Flow efficiency of ${Math.round(fe)}% means over ${100 - Math.round(fe)}% of lead time is non-value-adding wait or queue time.` });
  const ct = m.cycle_time ?? 0, sd = m.std_deviation ?? 0;
  if (sd > ct && ct > 0) anomalies.push({ sev: 'medium', text: `Lead time standard deviation (${fmtCT(sd)}) exceeds the average (${fmtCT(ct)}) — delivery is effectively unpredictable. A small number of extreme outliers are likely distorting the mean.` });
  const wa = (m.workflow_alignment ?? 0) * 100;
  if (wa > 0 && wa < 50) anomalies.push({ sev: 'medium', text: `Workflow alignment of ${Math.round(wa)}% means fewer than half of ${entityLabel.toLowerCase()}s follow the intended process path.` });

  const sevColor  = { high: '#BF2600', medium: '#974F0C', low: '#0052CC' };
  const sevBg     = { high: '#FFF3F0', medium: '#FFFAE6', low: '#E9F2FF' };
  const sevBorder = { high: '#FFBDAD', medium: '#FFE380', low: '#B3D4FF' };
  const sevIcon   = { high: '▲', medium: '◆', low: 'ℹ' };
  const sevTag    = { high: 'High Priority', medium: 'Attention', low: 'Info' };
  const sevTagBg  = { high: '#FFEBE6', medium: '#FFF0B3', low: '#DEEBFF' };

  // ── Card builder — left accent bar style ──────────────────────────────────
  // Each card: white bg, coloured 4px left border, label / big value / sublabel
  const card = (label, value, sublabel, accent = '#0052CC', valueDash = false) => {
    const isDash = value === '—';
    const displayColor = isDash ? '#97A0AF' : accent;
    return `
    <div style="
      background:#FFFFFF;
      border:1px solid #E5E8EC;
      border-left:4px solid ${isDash ? '#DFE1E6' : accent};
      border-radius:6px;
      padding:14px 16px 12px;
      min-width:130px;
      flex:1;
      box-shadow:0 1px 3px rgba(9,30,66,0.06);
      display:flex;flex-direction:column;justify-content:space-between;
      min-height:88px;
    ">
      <div style="font-size:10px;font-weight:700;color:#7A869A;text-transform:uppercase;letter-spacing:0.07em;margin-bottom:6px;">${label}</div>
      <div style="font-size:26px;font-weight:800;color:${displayColor};line-height:1;letter-spacing:-0.5px;margin-bottom:6px;">${value}</div>
      <div style="font-size:11px;color:#97A0AF;font-weight:500;">${sublabel}</div>
    </div>`;
  };

  // ── Section header — pill label + rule ───────────────────────────────────
  const sectionHead = (title, accent, icon) => `
    <div style="display:flex;align-items:center;gap:10px;margin:28px 0 10px;">
      <div style="
        display:inline-flex;align-items:center;gap:6px;
        background:${accent}18;border:1px solid ${accent}44;
        border-radius:999px;padding:4px 12px;flex-shrink:0;
      ">
        <span style="font-size:13px;line-height:1;">${icon}</span>
        <span style="font-size:11px;font-weight:800;letter-spacing:0.07em;text-transform:uppercase;color:${accent};">${title}</span>
      </div>
      <div style="flex:1;height:1px;background:linear-gradient(to right,${accent}30,transparent);"></div>
    </div>`;

  // ── Smart rework colour ───────────────────────────────────────────────────
  const reworkAccent = rework > 25 ? '#BF2600' : rework > 10 ? '#FF8B00' : '#006644';
  const waAccent = wa > 0 && wa < 50 ? '#BF2600' : '#006644';
  const feAccent = fe > 0 && fe < 15 ? '#BF2600' : fe >= 30 ? '#006644' : '#FF8B00';
  const waitAccent = (m.avg_wait_time ?? 0) > (m.avg_touch_time ?? 0) ? '#FF8B00' : '#42526E';

  return `
    <!-- ══ PROCESS METRICS ═══════════════════════════════════════════════════ -->
    <div style="margin-bottom:36px;">

      <!-- Section banner -->
      <div style="
        display:flex;align-items:center;gap:14px;
        padding:16px 20px;margin-bottom:20px;
        background:linear-gradient(135deg,#0052CC 0%,#0C66E4 100%);
        border-radius:8px;color:#fff;
      ">
        <div style="width:40px;height:40px;background:rgba(255,255,255,0.15);border-radius:8px;
                    display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M3 17h4V7H3zm7 0h4V3h-4zm7 0h4v-8h-4z"/></svg>
        </div>
        <div style="flex:1;">
          <div style="font-size:16px;font-weight:800;letter-spacing:-0.2px;">${processLabel} Metrics Dashboard</div>
          <div style="font-size:12px;opacity:0.8;margin-top:2px;">Aggregated totals across the full model · ${entityLabel} volumes, time, quality &amp; flow indicators</div>
        </div>
        <div style="font-size:11px;opacity:0.65;white-space:nowrap;">Generated ${new Date().toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'})}</div>
      </div>

      <!-- Consultant observations -->
      ${anomalies.length ? `
      <div style="margin-bottom:24px;">
        <div style="font-size:11px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#42526E;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
          <span style="display:inline-block;width:3px;height:14px;background:#FF8B00;border-radius:2px;"></span>
          Consultant Observations
        </div>
        ${anomalies.map(a => `
        <div style="
          display:flex;gap:12px;align-items:flex-start;
          padding:12px 16px;margin-bottom:8px;
          background:${sevBg[a.sev]};
          border:1px solid ${sevBorder[a.sev]};
          border-left:4px solid ${sevColor[a.sev]};
          border-radius:6px;
        ">
          <span style="
            font-size:10px;font-weight:800;letter-spacing:0.04em;text-transform:uppercase;
            color:${sevColor[a.sev]};white-space:nowrap;flex-shrink:0;margin-top:1px;
            background:${sevTagBg[a.sev]};border-radius:4px;padding:3px 8px;
          ">${sevIcon[a.sev]} ${sevTag[a.sev]}</span>
          <span style="font-size:13px;color:#172B4D;line-height:1.6;">${a.text}</span>
        </div>`).join('')}
      </div>` : ''}

      <!-- Work Item Metrics -->
      ${sectionHead(`${entityLabel} Metrics`, '#0052CC', '👤')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px;margin-bottom:4px;">
        ${card(`${entityLabel}s`, fmtNum(m.work_items), 'Total volume', '#0052CC')}
        ${card('Avg Lead Time', fmtCT(m.cycle_time), 'Creation to closure', '#DE350B')}
        ${card('Std Deviation', fmtCT(m.std_deviation), 'Lead time variability', '#8777D9')}
        ${card('% Re-work', fmtPct(m.rework_rate), `${entityLabel.toLowerCase()}s looping back`, reworkAccent)}
        ${card('Avg Work Time', val(m.avg_time_spent, fmtTime), 'Logged time per item', '#0747A6')}
      </div>

      <!-- Process Metrics -->
      ${sectionHead(`${processLabel} Metrics`, '#FFAB00', '⚙')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px;margin-bottom:4px;">
        ${card('Variants', fmtNum(m.variants), 'Distinct paths', '#FFAB00')}
        ${card('Activities', fmtNum(m.activities), 'Unique states used', '#00875A')}
        ${card('Steps', fmtNum(m.steps), 'Unique transitions', '#6554C0')}
        ${card('Avg Transitions', fmtDec(m.avg_transitions), `Per ${entityLabel.toLowerCase()}`, '#42526E')}
        ${card('Workflow Alignment', val(m.workflow_alignment, fmtPct), 'Following intended path', m.workflow_alignment ? waAccent : '#97A0AF')}
        ${card('Compliance', val(m.compliance, fmtPct), 'Meeting compliance rules', '#36B37E')}
      </div>

      <!-- Flow & Time -->
      ${sectionHead('Flow & Time Metrics', '#00A3BF', '⏱')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px;margin-bottom:4px;">
        ${card('Avg Cycle Time',   val(m.avg_cycle_time_started, fmtCT),  'Work start to done',        '#00A3BF')}
        ${card('Avg Resolution',   val(m.avg_resolution_time, fmtCT),     'Creation to first resolve',  '#1D7AFC')}
        ${card('Avg Touch Time',   val(m.avg_touch_time, fmtTime),        'Active work time',           '#36B37E')}
        ${card('Avg Wait Time',    val(m.avg_wait_time, fmtTime),         'Waiting states',             waitAccent)}
        ${card('Avg Queue Time',   val(m.avg_queue_time, fmtTime),        'Intake / backlog',           '#6554C0')}
        ${card('Avg Admin Time',   val(m.avg_admin_time, fmtTime),        'Post-resolution closure',    '#6B778C')}
        ${card('Flow Efficiency',  val(m.avg_flow_efficiency, fmtPct),    'Touch time / lead time',     feAccent)}
      </div>

      <!-- Collaboration (only if data present) -->
      ${(m.avg_customer_comments || m.avg_internal_comments || m.avg_worklogs) ? `
      ${sectionHead('Collaboration & Effort', '#36B37E', '💬')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px;margin-bottom:4px;">
        ${val(m.avg_customer_comments, fmtDec) !== '—' ? card('Customer Comments', fmtDec(m.avg_customer_comments), `Per ${entityLabel.toLowerCase()}`, '#FF8B00') : ''}
        ${val(m.avg_internal_comments, fmtDec) !== '—' ? card('Internal Comments', fmtDec(m.avg_internal_comments), `Per ${entityLabel.toLowerCase()}`, '#36B37E') : ''}
        ${val(m.avg_worklogs, fmtDec) !== '—' ? card('Avg Worklogs', fmtDec(m.avg_worklogs), `Per ${entityLabel.toLowerCase()}`, '#403294') : ''}
      </div>` : ''}

    </div>
    <!-- ══ END PROCESS METRICS ═══════════════════════════════════════════════ -->`.trim();
}

function formatVariantPath(path) {
  if (!path) return '';
  return escapeHtml(path).replace(/-&gt;/g, '<span style="color: #6B778C;"> → </span>');
}

function formatPercentage(value) {
  if (value == null || isNaN(value)) return 'N/A';
  return (value * 100).toFixed(1) + '%';
}

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return 'N/A';
  const days = Math.floor(seconds / (60 * 60 * 24));
  const hours = Math.floor((seconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((seconds % (60 * 60)) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function escapeHtml(text) {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

// Export the event log loader for use by export functions
export { loadEventLogForExport };